import { InfiniteQueryObserverResult } from '@tanstack/react-query';
import { AllMeasures } from 'convert-units';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { formatNumericValue, getUniqueOptions } from 'utils/general';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { useCreatePeriodicOperationUtils } from '@/modules/periodicTasks/components/Create/logic';
import { IPeriodicTasks } from '@/modules/periodicTasks/types';
import { SelectField, Typography } from '@/shared/components/atoms';
import EditableExpandableField from '@/shared/components/atoms/EditableExpandableField';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import LinkedInputs from '@/shared/components/molecules/LinkedInputs';
import { useDidUpdateEffect } from '@/shared/hooks/useDidUpdateEffect';
import { getMeasurementUnits, getUnitName } from '@/shared/lib/convert-units';
import { IGeneral } from '@/shared/types';

const Input: React.FC<{
  index: number;
  options: IPeriodicTasks.IOperationProductObject[];
  areaUnit?: string | null;
  measurementSystem?: string;
  handleAppend: IGeneral.IVoidFunction;
  handleRemove: IGeneral.IVoidFunction;
  fetchNextPage: () => Promise<
    InfiniteQueryObserverResult<IGeneral.ISuccessResponseDataSchema<unknown>>
  >;
}> = ({
  options,
  index,
  handleAppend,
  handleRemove,
  areaUnit,
  measurementSystem,
  fetchNextPage,
}) => {
  const { t } = useTranslation();

  const { getPlotsTotalWorkingArea, getRateFromAmount, getAmountFromRate } =
    useCreatePeriodicOperationUtils();

  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IPeriodicTasks.IOperationFormSchema>();

  const productsWatcher = watch(`products`);
  const rateWatcher = watch(`products.${index}.rate`);
  const selectedProduct = productsWatcher[index]?.product;
  const selectedUnit = productsWatcher[index]?.unit;

  const uniqueOptions =
    getUniqueOptions<IPeriodicTasks.IOperationProductObject>({
      allOptions: options,
      selectedOptions: productsWatcher.map(
        (item) => item.product as IPeriodicTasks.IOperationProductObject
      ),
      getValue: (value) => value?.product_id,
      exclude: productsWatcher[index].product,
    });

  const units = selectedProduct?.measurement_type
    ? getMeasurementUnits(
        selectedProduct.measurement_type.toLowerCase() as AllMeasures,
        measurementSystem?.toLowerCase()
      )
    : [];

  const totalWorkArea = getPlotsTotalWorkingArea(watch('plots'));

  const handleRateChange = (value: string) => {
    setValue(
      `products.${index}.amount`,
      getAmountFromRate(value, totalWorkArea),
      { shouldValidate: true }
    );
  };
  const handleAmountChange = (value: string) => {
    setValue(
      `products.${index}.rate`,
      getRateFromAmount(value, totalWorkArea),
      { shouldValidate: true }
    );
  };

  useDidUpdateEffect(() => {
    setValue(`products.${index}.unit`, null);
  }, [selectedProduct?.product_id]);

  return (
    <EditableExpandableField
      fieldArrayName='products'
      addFieldText={t('add-input')}
      editFieldText={t('edit-input')}
      addAnotherFieldText={t('add-another-input')}
      index={index}
      length={productsWatcher.length}
      collapsedHeader={
        <Typography>
          <span className='font-medium'>{selectedProduct?.product_name}</span>
          <span className='text-surface-10'>{` (${formatNumericValue(
            Number(rateWatcher)
          )} ${selectedUnit}/${areaUnit})`}</span>
        </Typography>
      }
      handleAppend={handleAppend}
      handleRemove={handleRemove}
    >
      <div className='flex flex-col gap-24'>
        <InfiniteSelectField
          id={`products.${index}.product`}
          errorMessageId={`products.${index}.product.product_id`}
          getLabel={(obj: IPeriodicTasks.IOperationProductObject) =>
            obj.product_name
          }
          getValue={(obj: IPeriodicTasks.IOperationProductObject) => obj}
          value={options.filter(
            (option) =>
              option.product_id === productsWatcher[index]?.product?.product_id
          )}
          fetchNextPage={fetchNextPage}
          borderColor={themeColors().outline.variant}
          label={t('input')}
          placeholder={t('input')}
          list={uniqueOptions}
          py='0.5rem'
          px='0.5rem'
          borderRadius='1rem'
          width='w-full min-w-0'
        />
        <SelectField
          id={`products.${index}.unit`}
          getLabel={(unit: IGeneral.LabelValuePair) => unit.label}
          getValue={(unit: IGeneral.LabelValuePair) => unit.value}
          borderColor={themeColors().outline.variant}
          label={t('unit')}
          placeholder={t('unit')}
          list={units.map((unit) => ({
            label: t(getUnitName(unit)),
            value: unit,
          }))}
          py='0.5rem'
          px='0.5rem'
          borderRadius='1rem'
          width='w-full min-w-0'
        />
        <div className='mt-8'>
          <Typography weight='medium'>{t('add-rate-or-amount')}</Typography>
          <Typography
            variant='subtitle2'
            className='mt-4 text-surface-10'
          >{`${t('work-area')}: ${totalWorkArea} ${t(
            areaUnit ?? ''
          )}`}</Typography>
        </div>
        <LinkedInputs
          alignment='row'
          inputProps={[
            {
              id: `products.${index}.rate`,
              onChange: (event) => handleRateChange(event.target.value),
              error: !!errors?.products?.[index]?.rate,
              errorMessage: errors?.products?.[index]?.rate?.message,
              label: t('rate-rate'),
              placeholder: t('rate-rate'),
              suffix: (
                <>
                  {selectedUnit && areaUnit
                    ? `${t(selectedUnit)}/${t(areaUnit)}`
                    : null}
                </>
              ),
            },
            {
              id: `products.${index}.amount`,
              onChange: (event) => handleAmountChange(event.target.value),
              error: !!errors?.products?.[index]?.amount,
              errorMessage: errors?.products?.[index]?.amount?.message,
              label: t('total-quantity'),
              placeholder: t('total-quantity'),
              suffix: <>{t(getUnitName(selectedUnit ?? undefined))}</>,
            },
          ]}
        />
      </div>
    </EditableExpandableField>
  );
};

export default Input;
